const { locales, defaultLocale, toContentLocale } = require("./locales");

/**
 * WHO-6641. The word in a Pro Buyer's client link (`/<word>/<token>`, WHO-6629 R13) per locale, so
 * a German client reads "auswahl" rather than the Dutch "selectie". `pages/selection/[token].tsx`
 * is the one route; every other locale serves its own word through a rewrite and 301s the other
 * words onto it, like `pro-buyer-paths.js`. The link is secret and never indexed, so there is no
 * sitemap entry, hreflang cluster or language switcher to localize.
 *
 * A dependency-free CJS leaf, like `value-check-paths.js`: `rewrites.js`, `redirects.js`,
 * `agent-markdown.js` and `scripts/generate-sitemap.ts` `require()` it, and the feature imports
 * it for the link it hands out.
 */
const SELECTION_WORDS = {
  en: "selection",
  nl: "selectie",
  de: "auswahl",
  fr: "selection",
  it: "selezione",
  es: "seleccion",
};

/** Every link handed out before WHO-6641 used the Dutch word, whatever the locale. */
const LEGACY_SELECTION_WORD = "selectie";

/** The internal route every locale renders. */
const SELECTION_PAGE = `/${SELECTION_WORDS[defaultLocale]}`;

/** Each word once (`selection` is English and French). */
const SELECTION_WORD_LIST = [...new Set(Object.values(SELECTION_WORDS))];

/**
 * The link's path in a locale, without the locale prefix. `en-us` uses the English word (WHO-5766);
 * unknown locales get the default.
 */
const getSelectionPath = locale =>
  `/${
    SELECTION_WORDS[toContentLocale(locale)] ?? SELECTION_WORDS[defaultLocale]
  }`;

/** A client link in any locale and under any word: `/selectie/x`, `/de/auswahl/x`, `/en-us/selection/x`. */
const SELECTION_PATH_PATTERN = new RegExp(
  `^(?:/[a-z]{2}(?:-[a-z]{2})?)?/(?:${SELECTION_WORD_LIST.join("|")})/`
);

const prefixedLocales = locales.filter(locale => locale !== defaultLocale);

/**
 * `/nl/selectie/<token>` serves `/nl/selection/<token>`. The last rule keeps the English links
 * handed out before WHO-6641 (`/selectie/<token>`) working. It has no `locale: false`: on Vercel
 * an unprefixed English request is matched as `/en/...` (WHO-5477), which only a rule Next
 * prefixes per locale reaches. Every prefixed locale answers before it, through its own rewrite
 * or a redirect below.
 */
const selectionRewrites = () => [
  ...prefixedLocales
    // `/fr` and `/en-us` use the English word, which is the route itself: nothing to rewrite.
    .filter(locale => getSelectionPath(locale) !== SELECTION_PAGE)
    .map(locale => ({
      source: `/${locale}${getSelectionPath(locale)}/:token`,
      destination: `/${locale}${SELECTION_PAGE}/:token`,
      locale: false,
    })),
  {
    source: `/${LEGACY_SELECTION_WORD}/:token`,
    destination: `${SELECTION_PAGE}/:token`,
  },
];

/**
 * `/de/selectie/<token>` and `/nl/selection/<token>` 301 to the locale's own word, so every link,
 * old ones included, ends on the client's word and no locale answers on two. Only mismatched
 * pairs are generated, so the pair each rewrite serves is never redirected. English gets no rule:
 * `/en/`-prefixed sources are forbidden (WHO-5477); its old links are served by the rewrite above.
 */
const selectionRedirects = () =>
  prefixedLocales.flatMap(locale => {
    const own = SELECTION_WORDS[toContentLocale(locale)];

    return SELECTION_WORD_LIST.filter(word => word !== own).map(word => ({
      source: `/${locale}/${word}/:token`,
      destination: `/${locale}/${own}/:token`,
      permanent: true,
      locale: false,
    }));
  });

module.exports = {
  SELECTION_WORDS,
  SELECTION_WORD_LIST,
  SELECTION_PAGE,
  SELECTION_PATH_PATTERN,
  getSelectionPath,
  selectionRewrites,
  selectionRedirects,
};
